// libs/ui-components/src/lib/modules/overlay/overlay-container.ts
var container;
function getContainer() {
  findOrCreateContainer();
  return container;
}
function findOrCreateContainer() {
  if (container) return;
  let overlayContainer = document.querySelector("#overlay-container");
  if (!overlayContainer) {
    overlayContainer = document.createElement("div");
    overlayContainer.id = "overlay-container";
  }
  container = overlayContainer;
}

// libs/ui-components/src/lib/modules/overlay/overlay-mobile-controller.ts
import { html, render } from "lit";
import { appendStyle, getMobileMatchMediaEmitter, isSafari, isStandalone, resizeObserver } from "@one-inch-community/core/lit";
import { asyncFrame } from "@one-inch-community/core/async";
import { applyColorBrightness, interpolateColorRange, getCssValue, setBrowserMetaColorFilter } from "@one-inch-community/core/theme";

// libs/ui-components/src/lib/modules/overlay/overlay-id-generator.ts
var id = 1;
function getOverlayId() {
  return id++;
}

// libs/ui-components/src/lib/modules/overlay/overlay-mobile-controller.ts
import {
  fromEvent,
  tap,
  merge,
  filter,
  map,
  distinctUntilChanged,
  switchMap,
  skip,
  takeUntil
} from "rxjs";
import { ScrollViewProviderElement } from "@one-inch-community/ui-components/scroll";
var OverlayMobileController = class {
  constructor(rootNodeName) {
    this.rootNodeName = rootNodeName;
    this.borderRadius = "8px";
    this.brightnessHalfView = ".7";
    this.scale = ".9";
    this.topOffsetPercent = -5;
    this.backgroundColor = "var(--color-background-bg-active)";
    this.backgroundColorDefault = "var(--color-background-bg-body)";
    this.container = getContainer();
    this.activeOverlayMap = /* @__PURE__ */ new Map();
    this.subscriptions = /* @__PURE__ */ new Map();
  }
  get isOpen() {
    return this.activeOverlayMap.size > 0;
  }
  isOpenOverlay(overlayId) {
    return this.activeOverlayMap.has(overlayId);
  }
  async open(openTarget) {
    const rootNode = document.querySelector(this.rootNodeName);
    const overlayContainer = this.createOverlayContainer(openTarget);
    await asyncFrame();
    const halfView = this.calculateIsHalfView(overlayContainer);
    this.appendStyleBeforeTransition(rootNode);
    await this.transition(overlayContainer, rootNode, false, halfView);
    this.appendStyleAfterTransition(rootNode, false, halfView);
    const id2 = getOverlayId();
    this.activeOverlayMap.set(id2, overlayContainer);
    this.subscribeOnEvents(id2);
    return id2;
  }
  async close(overlayId) {
    if (!this.activeOverlayMap.has(overlayId)) {
      return;
    }
    const overlayContainer = this.activeOverlayMap.get(overlayId);
    const rootNode = document.querySelector(this.rootNodeName);
    const halfView = this.calculateIsHalfView(overlayContainer);
    await this.transition(overlayContainer, rootNode, true, halfView);
    this.appendStyleAfterTransition(rootNode, true, halfView);
    this.unsubscribeOnResize(overlayId);
    overlayContainer.remove();
    this.activeOverlayMap.delete(overlayId);
    setBrowserMetaColorFilter(null);
  }
  createOverlayContainer(openTarget) {
    const overlayContainer = document.createElement(ScrollViewProviderElement.tagName);
    const overlayIndex = this.activeOverlayMap.size + 1;
    const offsetStep = 2;
    const innerHeight = isSafari() && isStandalone() ? window.innerHeight - 10 : window.innerHeight;
    overlayContainer.maxHeight = (100 - overlayIndex * offsetStep) * innerHeight / 100;
    overlayContainer.setAttribute("overlay-index", overlayIndex.toString());
    appendStyle(overlayContainer, {
      position: "fixed",
      display: "flex",
      width: "100vw",
      overflow: "hidden",
      alignItems: "flex-end",
      bottom: "0",
      left: "0",
      zIndex: "2000",
      boxSizing: "border-box",
      borderTopLeftRadius: "24px",
      borderTopRightRadius: "24px"
    });
    if (isSafari() && isStandalone()) {
      appendStyle(overlayContainer, {
        bottom: "10px"
      });
    }
    render(html`${openTarget}`, overlayContainer);
    this.container.appendChild(overlayContainer);
    return overlayContainer;
  }
  subscribeOnEvents(overlayId) {
    const overlayContainer = this.activeOverlayMap.get(overlayId);
    const rootNode = document.querySelector(this.rootNodeName);
    const subscription = merge(
      //
      getMobileMatchMediaEmitter().pipe(
        tap(() => this.updatePosition(overlayId))
      ),
      //
      resizeObserver(overlayContainer).pipe(
        filter(() => {
          const overlayIndex = Number(overlayContainer.getAttribute("overlay-index"));
          return overlayIndex === this.activeOverlayMap.size;
        }),
        map(() => this.calculateIsHalfView(overlayContainer)),
        distinctUntilChanged(),
        skip(1),
        switchMap((halfView) => {
          const rootNode2 = document.querySelector(this.rootNodeName);
          return this.transitionHalfView(rootNode2, halfView);
        })
      ),
      //
      fromEvent(this.container, "click").pipe(
        filter(() => {
          const overlayIndex = Number(overlayContainer.getAttribute("overlay-index"));
          return overlayIndex === this.activeOverlayMap.size;
        }),
        filter((event) => event.target === this.container),
        tap(() => this.updatePosition(overlayId))
      ),
      //
      fromEvent(overlayContainer, "touchstart", { passive: true }).pipe(
        filter(() => (overlayContainer.scrollTopFromConsumer ?? 0) === 0),
        switchMap((startEvent) => {
          const halfView = this.calculateIsHalfView(overlayContainer);
          const overlayContainerHeight = overlayContainer.clientHeight;
          const swipeMaxForClose = 30;
          const startPoint = startEvent.touches[0].clientY;
          const bgColorStart = getCssValue(this.backgroundColor);
          const bgColorEnd = getCssValue(this.backgroundColorDefault);
          let currentDelta = 0;
          let currentDeltaPoint = 0;
          let closeReady = false;
          let scale = Number(this.scale);
          let offset = this.topOffsetPercent;
          let bgColor = this.backgroundColor;
          return fromEvent(overlayContainer, "touchmove", { passive: true }).pipe(
            filter(() => !closeReady),
            tap((event) => {
              const currentPoint = event.touches[0].clientY;
              const deltaPoint = currentPoint - startPoint;
              const delta = deltaPoint * 100 / overlayContainerHeight;
              if (delta < 0) return;
              if (delta > swipeMaxForClose) {
                closeReady = true;
                this.updatePosition(overlayId);
              }
              currentDelta = delta;
              currentDeltaPoint = deltaPoint;
              overlayContainer.setAttribute("offset", delta.toString());
              appendStyle(overlayContainer, {
                transform: `translateY(${deltaPoint}px)`
              });
              if (!halfView) {
                scale = delta * (1 - Number(this.scale)) / swipeMaxForClose + Number(this.scale);
                offset = this.topOffsetPercent - delta * this.topOffsetPercent / swipeMaxForClose;
                bgColor = interpolateColorRange(bgColorStart, bgColorEnd, 0, 100, delta);
                rootNode.setAttribute("scale", scale.toString());
                rootNode.setAttribute("offset", offset.toString());
                rootNode.setAttribute("bgColor", bgColor.toString());
                appendStyle(rootNode, {
                  transform: `scale(${scale}) translate3d(0, ${offset}%, 0)`,
                  backgroundColor: bgColor
                });
              }
            }),
            takeUntil(merge(
              fromEvent(overlayContainer, "touchend", { passive: true }),
              fromEvent(overlayContainer, "touchcancel", { passive: true })
            ).pipe(
              switchMap(async (endEvent) => {
                if (currentDeltaPoint === 0) return;
                if (endEvent.timeStamp - startEvent.timeStamp < 300 && currentDelta > swipeMaxForClose / 2) {
                  this.updatePosition(overlayId);
                  return;
                }
                if (closeReady) return;
                await Promise.all([
                  overlayContainer.animate([
                    { transform: `translateY(${currentDeltaPoint}px)` },
                    { transform: `translateY(0px)` }
                  ], this.getDefaultAnimationOptions()).finished,
                  !halfView ? rootNode.animate([
                    { transform: `scale(${scale}) translate3d(0, ${offset}%, 0)`, backgroundColor: bgColor },
                    { transform: `scale(${this.scale}) translate3d(0, ${this.topOffsetPercent}%, 0)`, backgroundColor: this.backgroundColor }
                  ], this.getDefaultAnimationOptions()).finished : Promise.resolve()
                ]);
                overlayContainer.removeAttribute("offset");
                appendStyle(overlayContainer, {
                  transform: ``
                });
                if (!halfView) {
                  rootNode.removeAttribute("scale");
                  rootNode.removeAttribute("offset");
                  rootNode.removeAttribute("bgColor");
                  appendStyle(rootNode, {
                    transform: `scale(${this.scale}) translate3d(0, ${this.topOffsetPercent}%, 0)`,
                    backgroundColor: this.backgroundColor
                  });
                }
              })
            ))
          );
        })
      )
    ).subscribe();
    this.subscriptions.set(overlayId, subscription);
  }
  unsubscribeOnResize(overlayId) {
    if (!this.subscriptions.has(overlayId)) return;
    const subscription = this.subscriptions.get(overlayId);
    if (subscription.closed) return;
    subscription.unsubscribe();
  }
  updatePosition(overlayId) {
    if (!this.subscriptions.has(overlayId)) return;
    this.close(overlayId).catch();
  }
  calculateIsHalfView(element) {
    const height = element.clientHeight;
    const maxHeight = element.maxHeight ?? 0;
    return height * 100 / maxHeight < 90;
  }
  getDefaultAnimationOptions() {
    return {
      duration: 500,
      easing: "cubic-bezier(.2, .8, .2, 1)"
    };
  }
  async transition(overlayContainer, rootNode, isBack, halfView) {
    const scale = rootNode.getAttribute("scale") ?? this.scale;
    const brightnessHalfView = this.brightnessHalfView;
    const offset = Number(rootNode.getAttribute("offset") ?? this.topOffsetPercent);
    const backgroundColor = rootNode.getAttribute("bgColor") ?? this.backgroundColor;
    const mainOverlayOffset = overlayContainer.getAttribute("offset") ?? "0";
    const transitionOverlayContainerStart = () => ({ transform: `translate3d(0, ${isBack ? mainOverlayOffset : "100"}%, 0)` });
    const transitionOverlayContainerEnd = () => ({ transform: `translate3d(0, ${!isBack ? mainOverlayOffset : "100"}%, 0)` });
    const transitionRootNodeStart = () => ({
      filter: halfView ? `brightness(${isBack ? brightnessHalfView : "1"})` : "",
      transform: halfView ? "" : `scale(${isBack ? scale : "1"}) translate3d(0, ${isBack ? offset : "0"}%, 0)`,
      borderRadius: halfView ? "" : isBack ? this.borderRadius : "0",
      backgroundColor: halfView ? "" : isBack ? backgroundColor : this.backgroundColorDefault
    });
    const transitionRootNodeEnd = () => ({
      filter: halfView ? `brightness(${!isBack ? brightnessHalfView : "1"})` : "",
      transform: halfView ? "" : `scale(${!isBack ? scale : "1"}) translate3d(0, ${!isBack ? offset : "0"}%, 0)`,
      borderRadius: halfView ? "" : !isBack ? this.borderRadius : "0",
      backgroundColor: halfView ? "" : !isBack ? backgroundColor : this.backgroundColorDefault
    });
    this.changeBrowserMetaColor(halfView, isBack);
    return await Promise.all([
      overlayContainer.animate([
        transitionOverlayContainerStart(),
        transitionOverlayContainerEnd()
      ], this.getDefaultAnimationOptions()).finished,
      rootNode.animate([
        transitionRootNodeStart(),
        transitionRootNodeEnd()
      ], this.getDefaultAnimationOptions()).finished
    ]);
  }
  async transitionHalfView(rootNode, isBack) {
    const scale = this.scale;
    const brightnessHalfView = this.brightnessHalfView;
    const offset = this.topOffsetPercent;
    const transitionRootNodeStart = () => ({
      filter: `brightness(${!isBack ? brightnessHalfView : "1"})`,
      transform: `scale(${isBack ? scale : "1"}) translate3d(0, ${isBack ? offset : "0"}%, 0)`,
      borderRadius: isBack ? this.borderRadius : "0",
      backgroundColor: isBack ? this.backgroundColor : this.backgroundColorDefault
    });
    const transitionRootNodeEnd = () => ({
      filter: `brightness(${isBack ? brightnessHalfView : "1"})`,
      transform: `scale(${!isBack ? scale : "1"}) translate3d(0, ${!isBack ? offset : "0"}%, 0)`,
      borderRadius: !isBack ? this.borderRadius : "0",
      backgroundColor: !isBack ? this.backgroundColor : this.backgroundColorDefault
    });
    this.changeBrowserMetaColor(isBack, false);
    await rootNode.animate([
      transitionRootNodeStart(),
      transitionRootNodeEnd()
    ], this.getDefaultAnimationOptions()).finished;
    appendStyle(rootNode, transitionRootNodeEnd());
  }
  appendStyleBeforeTransition(rootNode) {
    appendStyle(rootNode, {
      willChange: "filter, transform",
      overflow: "hidden",
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      pointerEvents: "none"
    });
    rootNode.removeAttribute("scale");
    rootNode.removeAttribute("offset");
    rootNode.removeAttribute("bgColor");
  }
  appendStyleAfterTransition(rootNode, isBack, halfView) {
    if (isBack) {
      return this.resetRootNodeStyle(rootNode);
    }
    appendStyle(rootNode, {
      filter: halfView ? `brightness(${this.brightnessHalfView})` : "",
      transform: halfView ? "" : `scale(${this.scale}) translate3d(0, ${this.topOffsetPercent}%, 0)`,
      borderRadius: halfView ? "" : this.borderRadius,
      backgroundColor: halfView ? "" : this.backgroundColor
    });
  }
  changeBrowserMetaColor(halfView, isBack) {
    setBrowserMetaColorFilter((color, isDarkTheme) => {
      if (halfView && !isBack) {
        return applyColorBrightness(color, parseFloat(this.brightnessHalfView));
      }
      return color;
    });
  }
  resetRootNodeStyle(rootNode) {
    appendStyle(rootNode, {
      filter: "",
      transform: "",
      position: "",
      willChange: "",
      overflow: "",
      top: "",
      left: "",
      right: "",
      bottom: "",
      zIndex: "",
      pointerEvents: "",
      backgroundColor: "",
      borderRadius: ""
    });
  }
};

// libs/ui-components/src/lib/modules/overlay/overlay-desktop-controller.ts
import { html as html2, render as render2 } from "lit";
import { appendStyle as appendStyle2, isRTLCurrentLocale } from "@one-inch-community/core/lit";
import { fromEvent as fromEvent2, Subscription as Subscription2 } from "rxjs";
import { ScrollViewProviderElement as ScrollViewProviderElement2 } from "@one-inch-community/ui-components/scroll";
var OverlayDesktopController = class {
  constructor(target, rootNodeName) {
    this.target = target;
    this.rootNodeName = rootNodeName;
    this.activeOverlayMap = /* @__PURE__ */ new Map();
    this.subscriptions = /* @__PURE__ */ new Map();
    this.container = getContainer();
  }
  get isOpen() {
    return this.activeOverlayMap.size > 0;
  }
  isOpenOverlay(overlayId) {
    return this.activeOverlayMap.has(overlayId);
  }
  async open(openTarget) {
    const position = await this.getPosition(openTarget);
    const overlayContainer = this.createOverlayContainer(openTarget);
    overlayContainer.maxHeight = position[2];
    if (this.target === "center") {
      appendStyle2(overlayContainer, {
        top: `0px`,
        left: `0px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%"
      });
    } else {
      appendStyle2(overlayContainer, {
        top: `${position[1]}px`,
        left: `${position[0]}px`,
        borderRadius: "24px",
        boxShadow: `0 -3px 4px 0 var(--primary-12), 0 6px 12px 0 var(--primary-12)`
      });
    }
    await this.animateEnter(overlayContainer, position[3]);
    const id2 = getOverlayId();
    this.activeOverlayMap.set(id2, overlayContainer);
    this.subscribe(id2);
    return id2;
  }
  async close(overlayId) {
    if (!this.activeOverlayMap.has(overlayId)) {
      return;
    }
    const overlayContainer = this.activeOverlayMap.get(overlayId);
    await this.animateLeave(overlayContainer);
    this.unsubscribe(overlayId);
    overlayContainer.remove();
    this.activeOverlayMap.delete(overlayId);
  }
  async getPosition(openTarget) {
    const offset = 8;
    if (this.target === "center") {
      return [0, 0, window.innerHeight - offset * 2, null];
    }
    const rect = this.target.getBoundingClientRect();
    const rectContent = await this.getRect(openTarget);
    let left = rect.right - rectContent.width;
    if (left <= 0) {
      left = rect.left;
    }
    const top = rect.top + rect.height + offset;
    const maxHeight = window.innerHeight - top - offset;
    return [left, top, maxHeight, rect];
  }
  async getRect(openTarget) {
    const el = this.createOverlayContainer(openTarget);
    appendStyle2(el, {
      visibility: "hidden"
    });
    document.body.appendChild(el);
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const rect = el.getBoundingClientRect();
    el.remove();
    return rect;
  }
  createOverlayContainer(openTarget) {
    const overlayContainer = document.createElement(ScrollViewProviderElement2.tagName);
    appendStyle2(overlayContainer, {
      position: "absolute",
      display: "flex",
      overflow: "hidden",
      alignItems: "flex-end",
      width: "fit-content",
      height: "fit-content",
      zIndex: "2000"
    });
    render2(html2`${openTarget}`, overlayContainer);
    this.container.appendChild(overlayContainer);
    return overlayContainer;
  }
  subscribe(overlayId) {
    const subscription = new Subscription2();
    const overlayContainer = this.activeOverlayMap.get(overlayId);
    if (!overlayContainer) return;
    if (this.target === "center") {
      subscription.add(
        fromEvent2(overlayContainer, "click").subscribe((event) => {
          if (event.target !== overlayContainer) return;
          this.close(overlayId).catch();
        })
      );
      return;
    }
    subscription.add(
      fromEvent2(window, "resize").subscribe(() => this.close(overlayId).catch())
    );
    const rootNode = document.querySelector(this.rootNodeName);
    subscription.add(
      fromEvent2(rootNode, "scroll").subscribe(() => this.updatePosition(overlayId))
    );
    subscription.add(
      fromEvent2(overlayContainer, "click").subscribe((event) => {
        event.stopPropagation();
        event.preventDefault();
      })
    );
    subscription.add(
      fromEvent2(document, "click").subscribe(() => {
        this.close(overlayId).catch();
      })
    );
    this.subscriptions.set(overlayId, subscription);
  }
  unsubscribe(overlayId) {
    if (!this.subscriptions.has(overlayId)) return;
    const subscription = this.subscriptions.get(overlayId);
    if (subscription.closed) return;
    subscription.unsubscribe();
  }
  updatePosition(overlayId) {
    if (this.target === "center") return;
    if (!this.activeOverlayMap.has(overlayId)) {
      return;
    }
    const overlayContainer = this.activeOverlayMap.get(overlayId);
    const rect = this.target.getBoundingClientRect();
    const rectContent = overlayContainer.getBoundingClientRect();
    const top = rect.top + rect.height + 8;
    const left = rect.right - rectContent.width;
    appendStyle2(overlayContainer, {
      top: `${top}px`,
      left: `${left}px`
    });
  }
  async animateEnter(overlayContainer, targetRect) {
    const options = {
      duration: 500,
      easing: "cubic-bezier(.2, .8, .2, 1)"
    };
    if (this.target === "center") {
      const rootNode = document.querySelector(this.rootNodeName);
      await Promise.all([
        rootNode.animate([
          { filter: "blur(0)" },
          { filter: "blur(3px)" }
        ], options),
        overlayContainer.animate([
          { transform: "scale(.3)", opacity: 0.3 },
          { transform: "scale(1)", opacity: 1 }
        ], options).finished
      ]);
      appendStyle2(rootNode, {
        filter: "blur(3px)"
      });
      return;
    }
    if (!targetRect) {
      throw new Error("");
    }
    await overlayContainer.animate([
      { transform: `translate3d(${isRTLCurrentLocale() ? -5 : 5}%, -5%, 0)`, opacity: 0.3 },
      { transform: "translate3d(0, 0, 0)", opacity: 1 }
    ], options).finished;
  }
  async animateLeave(overlayContainer) {
    const options = {
      duration: 500,
      easing: "cubic-bezier(.2, .8, .2, 1)"
    };
    if (this.target === "center") {
      const rootNode = document.querySelector(this.rootNodeName);
      await Promise.all([
        rootNode.animate([
          { filter: "blur(3px)" },
          { filter: "blur(0)" }
        ], options),
        overlayContainer.animate([
          { transform: "scale(1)", opacity: 1 },
          { transform: "scale(0)", opacity: 0 }
        ], {
          ...options,
          duration: 300
        }).finished
      ]);
      appendStyle2(rootNode, {
        filter: ""
      });
      return;
    }
    await overlayContainer.animate([
      { transform: "translate3d(0, 0, 0)", opacity: 1 },
      { transform: `translate3d(${isRTLCurrentLocale() ? -5 : 5}%, -5%, 0)`, opacity: 0 }
    ], options).finished;
  }
};

// libs/ui-components/src/lib/modules/overlay/overlay-controller.ts
import { getMobileMatchMedia } from "@one-inch-community/core/lit";
var OverlayController = class {
  constructor(rootNodeName, target) {
    this.mobileMedia = getMobileMatchMedia();
    this.mobileOverlay = new OverlayMobileController(rootNodeName);
    this.desktopOverlay = new OverlayDesktopController(target, rootNodeName);
  }
  get isOpen() {
    if (this.mobileMedia.matches) {
      return this.mobileOverlay.isOpen;
    }
    return this.desktopOverlay.isOpen;
  }
  isOpenOverlay(overlayId) {
    if (this.mobileMedia.matches) {
      return this.mobileOverlay.isOpenOverlay(overlayId);
    }
    return this.desktopOverlay.isOpenOverlay(overlayId);
  }
  async open(openTarget) {
    if (this.mobileMedia.matches) {
      return await this.mobileOverlay.open(openTarget);
    }
    return await this.desktopOverlay.open(openTarget);
  }
  async close(overlayId) {
    if (this.mobileMedia.matches) {
      return await this.mobileOverlay.close(overlayId);
    }
    return await this.desktopOverlay.close(overlayId);
  }
};
export {
  OverlayController,
  OverlayDesktopController,
  OverlayMobileController,
  getContainer
};
//# sourceMappingURL=index.esm.js.map
